import { useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';

import type { Agent, Proposal, ProposalDraft } from '../../api/agentSchemas';
import { approveProposal, dismissProposal, editProposal } from '../../api/endpoints/agents';
import { createIdempotencyKeyHolder } from '../../api/idempotency';
import { ApiError } from '../../api/client';
import { Drawer } from '../../admin/components/Drawer';
import { ErrorBanner } from '../../components/ErrorBanner';
import { AgentMiniOrb, agentName } from './AgentBoardLists';
import { ON_APPROVE } from './agentLook';
import { MessageDraftEditor, OfferDraftEditor } from './DraftEditors';
import { SendList } from './SendList';

type ProposalDrawerProps = {
  proposal: Proposal | null;
  agents: readonly Agent[];
  onClose: () => void;
  /** The server's copy after an edit, an approval or a dismissal. */
  onSettled: (proposal: Proposal) => void;
  /** Someone else changed it: fetch the board again. */
  onReload: () => void;
};

export function ProposalDrawer({ proposal, agents, onClose, onSettled, onReload }: ProposalDrawerProps) {
  return (
    <Drawer open={Boolean(proposal)} title={proposal?.title ?? ''} onClose={onClose}>
      {proposal ? (
        // Keyed by version, so a fresh copy from the server resets the local draft.
        <ProposalReview
          key={`${proposal.id}:${proposal.version}:${proposal.status}`}
          proposal={proposal}
          agents={agents}
          onSettled={onSettled}
          onReload={onReload}
        />
      ) : null}
    </Drawer>
  );
}

export type ReviewProps = Omit<ProposalDrawerProps, 'proposal' | 'onClose'> & { proposal: Proposal };

export function ProposalReview({ proposal, agents, onSettled, onReload }: ReviewProps) {
  const [draft, setDraft] = useState<ProposalDraft>(proposal.draft);
  const approveKey = useRef(createIdempotencyKeyHolder()).current;
  const editKey = useRef(createIdempotencyKeyHolder()).current;
  const dismissKey = useRef(createIdempotencyKeyHolder()).current;
  const open = proposal.status === 'pending';
  const edited = JSON.stringify(draft) !== JSON.stringify(proposal.draft);

  // The last copy the server confirmed: a retry after a failed approval must not re-save an old version.
  const saved = useRef(proposal);

  // An edited draft is saved first (a new version), then that exact version is approved.
  const approval = useMutation({
    mutationFn: async () => {
      let current = saved.current;
      if (JSON.stringify(draft) !== JSON.stringify(current.draft)) {
        current = await editProposal(current.id, { expectedVersion: current.version, draft }, editKey.ensure());
        saved.current = current;
      }
      return approveProposal(current.id, current.version, approveKey.ensure());
    },
    meta: { exito: 'Propuesta aprobada' },
    onSuccess: onSettled,
    onSettled: () => {
      editKey.reset();
      approveKey.reset();
    },
  });
  const dismissal = useMutation({
    mutationFn: () => dismissProposal(saved.current.id, saved.current.version, dismissKey.ensure()),
    onSuccess: onSettled,
    onSettled: () => dismissKey.reset(),
  });
  const busy = approval.isPending || dismissal.isPending;
  const error = approval.error ?? dismissal.error;

  return (
    <div className="agents-review">
      <div className="agents-review-head">
        <AgentMiniOrb agentId={proposal.agentId} size={44} />
        <div>
          <p className="row-title">{agentName(agents, proposal.agentId)}</p>
          <p className="row-sub">{statusLine(proposal)}</p>
        </div>
      </div>

      <p className="agents-reason">{proposal.reason}</p>
      {proposal.evidence.length > 0 ? (
        <dl className="agents-facts agents-evidence">
          {proposal.evidence.map((row) => (
            <div key={row.label} className="agents-fact">
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {draft.kind === 'offer' ? (
        <OfferDraftEditor draft={draft} disabled={!open || busy} onChange={setDraft} />
      ) : (
        <MessageDraftEditor proposal={proposal} draft={draft} disabled={!open || busy} onChange={setDraft} />
      )}

      {open ? (
        <p className="notice agents-on-approve">
          <strong>Al aprobar:</strong> {ON_APPROVE[proposal.kind]}
        </p>
      ) : null}
      {error ? <ReviewError error={error} onReload={onReload} /> : null}

      {open ? (
        <div className="agents-review-actions">
          <button type="button" className="btn" disabled={busy} onClick={() => approval.mutate()}>
            {approval.isPending ? 'Aprobando…' : edited ? 'Guardar y aprobar' : 'Aprobar'}
          </button>
          <button type="button" className="btn secondary" disabled={busy} onClick={() => dismissal.mutate()}>
            Descartar
          </button>
        </div>
      ) : (
        <ClosedResult proposal={proposal} />
      )}
    </div>
  );
}

function ClosedResult({ proposal }: { proposal: Proposal }) {
  if (proposal.status === 'dismissed') return <p className="row-sub">La descartaste. No se hizo nada.</p>;
  return (
    <div className="agents-result">
      {proposal.result ? <p className="notice">{proposal.result.summary}</p> : null}
      {proposal.result?.measured ? <p className="agents-measured">{proposal.result.measured}</p> : null}
      {proposal.kind === 'offer' ? (
        <Link to="/ofertas" className="btn small secondary">
          Ver en Ofertas
        </Link>
      ) : (
        <SendList proposal={proposal} />
      )}
    </div>
  );
}

function ReviewError({ error, onReload }: { error: Error; onReload: () => void }) {
  const stale = error instanceof ApiError && error.status === 409 && error.code === 'proposal_changed';
  return (
    <div className="agents-error">
      <ErrorBanner message={error instanceof ApiError ? error.message : 'No pudimos completar eso ahora mismo.'} />
      {stale ? (
        <button type="button" className="btn small secondary" onClick={onReload}>
          Ver la última versión
        </button>
      ) : null}
    </div>
  );
}

function statusLine(proposal: Proposal): string {
  if (proposal.status === 'pending') return 'Necesita revisión';
  if (proposal.status === 'dismissed') return 'Descartada';
  return 'Aprobada';
}
